import { authResponseSchema, UserDto } from "@reactive-resume/dto";
import { LoaderFunction, redirect } from "react-router-dom";

import { RESUMES_KEY, USER_KEY } from "@/client/constants/query-keys";
import {
  isNewAccount,
  trackClickAndRegister,
  trackSubmitRegisterForm,
  type RegisterMethod,
} from "@/client/libs/analytics";
import { checkoutPathForPlan, peekSelectedPlan } from "@/client/libs/selected-plan";
import { queryClient, resetResumeQueries } from "@/client/libs/query-client";
import { fetchResumes } from "@/client/services/resume/resumes";
import { fetchUser } from "@/client/services/user";
import { useAuthStore } from "@/client/stores/auth";

const oauthMethod = (provider: UserDto["provider"]): RegisterMethod | null => {
  if (provider === "github" || provider === "google") return provider;
  return null;
};

export const authLoader: LoaderFunction<UserDto> = async ({ request }) => {
  const callbackUrl = new URL(request.url);
  const status = callbackUrl.searchParams.get("status");
  const isNewSignup = callbackUrl.searchParams.get("signup") === "1";

  const { success } = authResponseSchema.pick({ status: true }).safeParse({ status });

  if (!success) return redirect("/auth/login");

  if (status === "2fa_required") {
    return redirect("/auth/verify-otp");
  }

  resetResumeQueries();
  queryClient.removeQueries({ queryKey: USER_KEY });

  const user = await queryClient.fetchQuery({
    queryKey: USER_KEY,
    queryFn: fetchUser,
  });

  if (!user) {
    return redirect("/auth/login");
  }

  if (status === "authenticated") {
    useAuthStore.setState({ user });
    queryClient.setQueryData(USER_KEY, user);

    const method = oauthMethod(user.provider);
    if (isNewSignup && method && isNewAccount(user.createdAt)) {
      trackSubmitRegisterForm(user.id, method, user.email);
      trackClickAndRegister(user.id, method);
    }

    if (!user.planTier) {
      const selected = peekSelectedPlan();
      if (selected) return redirect(checkoutPathForPlan(selected.plan, selected.interval));
    }

    try {
      const resumes = await queryClient.fetchQuery({
        queryKey: RESUMES_KEY,
        queryFn: fetchResumes,
      });

      if (resumes.length > 0) return redirect("/dashboard");

      const firstName = user.name?.trim().split(/\s+/)[0] ?? "";
      const params = new URLSearchParams({ welcome: "1" });
      if (firstName) params.set("name", firstName);
      if (!user.planTier) return redirect(`/dashboard/resumes?${params.toString()}`);
      return redirect(`/onboarding?${params.toString()}`);
    } catch {
      const firstName = user.name?.trim().split(/\s+/)[0] ?? "";
      const params = new URLSearchParams({ welcome: "1" });
      if (firstName) params.set("name", firstName);
      if (!user.planTier) return redirect(`/dashboard/resumes?${params.toString()}`);
      return redirect(`/onboarding?${params.toString()}`);
    }
  }
};
